import { faCloudDownloadAlt, faPlus, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, styled, Typography } from '@mui/material'
import { ChangeEvent, useId, useState } from 'react'
import './styles.css'
import { sxButtonLabel, sxButtonText, sxButtonTextAccessibility } from './sxProps'
import DefaultButton from '../../../../components/Accessibility/DefaultButton'
import ModalAction from '../../../../components/ModalAction'
import ModalFeedBack from '../../../../components/ModalFeedBack'
import { useAccessibility } from '../../../../Store/contexts/AccessibilityContext'
import { createPortal } from 'react-dom'
import { getFileExtension } from '../../../../utils/MimeType'

export interface Anexo {
  arquivo: string,
  nomeArquivo: string,
  tipoArquivo: string,
  numAnexo: string | null,
}

interface AnexoButtonProps {
  adicionarAnexo: (anexos: Array<Anexo>) => void,
  removerAnexo: (anexoIndex: number) => void,
  verAnexo: (anexoIndex: number) => void,
  anexos: Array<Anexo>,
  listContainerId: string;
  hideUploadButton?: boolean;
  allowMultipleUploads?: boolean;
}

const AnexoButton = (props: AnexoButtonProps) => {

  const accessibility = useAccessibility();

  const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
  });

  const buttonId = useId();

  const [modalAction, setModalAction] = useState<null | {
    handleShowModal?: () => void; textOutlinedButton: string; textContainedButton: string;
    title: string; subTitle?: string; handleOutlinedButton?: () => void; handleContainedButton?: () => void; icon: "error" | "success" | "warning"
  }>(null);
  const [modalFeedback, setModalFeedback] = useState<null | {
    icon?: 'error' | 'success' | 'warning', title?: string, textButton?: string, subTitle?: string,
    closeAction: () => void, showButtonModal: boolean, buttonAction?: () => void
  }>(null);

  const openModalDeleteAnexo = (anexo: Anexo, index: number) => {
    setModalAction({
      title: 'Excluir anexo',
      subTitle: `Tem certeza que deseja excluir o anexo ${anexo.nomeArquivo}? Esta ação não pode ser revertida e terá impacto na análise da sua guia.`,
      textOutlinedButton: 'Cancelar',
      textContainedButton: 'Excluir anexo',
      handleContainedButton: () => removeAnexo(index),
      handleOutlinedButton: handleCloseModalAction,
      handleShowModal: handleCloseModalAction,
      icon: 'warning'
    })
  }

  const openModalFeedback = (
    icon: 'error' | 'success' | 'warning', title: string, subtitle: string, textButton: string, closeAction: () => void, buttonAction: () => void
  ) => {
    setModalFeedback({
      icon: icon,
      title: title,
      subTitle: subtitle,
      textButton: textButton,
      showButtonModal: true,
      closeAction: closeAction,
      buttonAction: buttonAction
    });
  }

  const closeModalFeedback = () => {
    setModalFeedback(null);

    setTimeout(() => {
      const buttonElem = document.getElementById(buttonId);
      if (buttonElem) {
        buttonElem.focus();
      }
    }, 0);
  }

  const handleCloseModalAction = () => {
    setModalAction(null);
  }

  const handleFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const anexos: Array<Anexo> = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.size > 4 * 1024 * 1024) {
        openModalFeedback('warning', '', 'O arquivo selecionado é muito grande. Por favor, selecione um arquivo menor que 4MB.', 'Fechar', closeModalFeedback, closeModalFeedback);
        return;
      }

      if (!(/\.(pdf|jpg|jpeg|png)$/i).test(file.name)) {
        openModalFeedback(
          'warning',
          '',
          'Somente são aceitos arquivos nos formatos .pdf, .jpg, jpeg ou .png',
          'Fechar',
          closeModalFeedback,
          closeModalFeedback
        );
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        const [prefix, base64Data] = base64String.split(',');
        anexos.push({
          nomeArquivo: file.name,
          arquivo: base64Data,
          tipoArquivo: getFileExtension(prefix),
          numAnexo: null,
        });
        if (anexos.length === files.length) {
          if (props.allowMultipleUploads) {
            props.adicionarAnexo([...props.anexos, ...anexos]);
          } else {
            props.adicionarAnexo(anexos);
          }
        }
      };
      reader.readAsDataURL(file);
    }
    // event.target.value = ''; não se deixo isso 
  };

  const removeAnexo = (posicao: number) => {
    props.removerAnexo(posicao);
    handleCloseModalAction();
  };

  return (
    <>
      {props.anexos.length > 0 && (
        <div className='guia-anexos-list-container'>
          {props.anexos.map((anexo, index) => (
            <Button
              onClick={() => props.verAnexo(index)}
              variant='text'
              sx={accessibility.contrastMode ? sxButtonTextAccessibility : sxButtonText}
              key={index}
              endIcon={
                <div className="delete-icon">
                  <FontAwesomeIcon icon={faTimes} onClick={e => { e.stopPropagation(); openModalDeleteAnexo(anexo, index) }} />
                </div>
              }
              aria-label={`Remover arquivo ${anexo.nomeArquivo}`}
            >
              <Typography sx={sxButtonLabel}>{anexo.nomeArquivo}</Typography>
            </Button>
          ))}
        </div>
      )}
      {!props.hideUploadButton && (
        <div className='container-anexo-button'>
          {props.allowMultipleUploads ? (
            <button
              id={buttonId}
              style={{ ...accessibility.textStyleAccessibility }}
              className="adicionar-anexo-button"
              onClick={() => document.getElementById(`file-input-${buttonId}`)?.click()}
              aria-label="Adicionar mais documentos"
            >
              <FontAwesomeIcon icon={faPlus} style={{ ...accessibility.buttonTextStyleAccessibility, marginRight: '5px' }} />
              Adicionar mais documentos
              <VisuallyHiddenInput
                id={`file-input-${buttonId}`}
                type="file"
                accept=".pdf,.doc,.docx,.png,.jpg"
                onChange={handleFileInputChange}
                multiple
                tabIndex={-1}
                aria-label="Anexar arquivos"
              />
            </button>
          ) : (
            <DefaultButton
              variant='outlined'
              id={buttonId}
              startIcon={<FontAwesomeIcon icon={faCloudDownloadAlt} style={accessibility.buttonTextStyleAccessibility} />}
              role={undefined}
              component="label"
              onKeyDown={e => { if (e.key === 'Enter') { e?.currentTarget.click(); } }}
            >
              <b style={accessibility.buttonTextStyleAccessibility} aria-hidden="true">ANEXAR DOCUMENTO</b>
              <VisuallyHiddenInput
                type="file"
                accept=".pdf,.doc,.docx,.png,.jpg"
                onChange={handleFileInputChange}
                multiple
                tabIndex={-1}
                aria-label="Anexar arquivos"
              />
            </DefaultButton>
          )}
        </div>
      )}
      <ModalFeedBack
        showModal={!!modalFeedback}
        handleShowModal={modalFeedback?.closeAction}
        title={modalFeedback?.title as string}
        subTitle={modalFeedback?.subTitle as string}
        icon={modalFeedback?.icon as 'success' | 'error' | 'warning'}
        showButtonModal={modalFeedback?.showButtonModal}
        textButton={modalFeedback?.textButton as string}
        handleButtonModal={modalFeedback?.buttonAction}
      />
      <ModalAction
        showModal={!!modalAction}
        handleShowModal={modalAction?.handleShowModal}
        icon={modalAction?.icon}
        textOutlinedButton={modalAction?.textOutlinedButton || ''}
        textContainedButton={modalAction?.textContainedButton || ''}
        title={modalAction?.title || ''}
        subTitle={modalAction?.subTitle || ''}
        handleOutlinedButton={modalAction?.handleOutlinedButton}
        handleContainedButton={modalAction?.handleContainedButton}
      />
    </>
  )
}

export default AnexoButton